import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { Shield, Save, ArrowLeft, Download, Settings, Sun, Moon } from 'lucide-react'
import { registerPushNotifications, unregisterPushNotifications, isPushRegistered } from '../lib/pushNotifications'
import { clearForegroundNotifyCache } from '../lib/foregroundNotify'
import { applyMode, getStoredMode, setStoredMode } from '../lib/displayMode'

import { isPlatformOrGlobalGm } from '../lib/roleHelpers'
import { Bell, BellOff } from 'lucide-react'
import { validateDisplayName, validateCallsign } from '../lib/validation'
import { QRCode } from 'react-qr-code'
import M55Mark from '../components/M55Mark'

const A = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(ellipse at 50% 10%, rgba(77,214,255,.08), transparent 60%), #0e1116',
    color: '#eaf2fb',
    fontFamily: "'Saira Condensed', 'Rajdhani', sans-serif",
  },
  nav: {
    background: 'linear-gradient(135deg, #2a2e36, #1a1d22)',
    borderBottom: '1px solid #4a5058',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  display: { fontFamily: "'Orbitron', 'Rajdhani', sans-serif", letterSpacing: '0.12em', textTransform: 'uppercase' },
  body: { fontFamily: "'Saira Condensed', 'Rajdhani', sans-serif" },
  mono: { fontFamily: "'IBM Plex Mono', monospace" },
  accent: { color: '#ff7a18' },
  holo: { color: '#4dd6ff' },
  text: { color: '#eaf2fb' },
  muted: { color: '#9aa6b0' },
  faded: { color: '#5a6470' },
  panel: {
    background: 'linear-gradient(135deg, #2a2e36, #1a1d22)',
    border: '1px solid #4a5058',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.06), inset 0 -1px 0 rgba(0,0,0,.4), 0 4px 12px rgba(0,0,0,.4)',
    padding: '24px',
    marginBottom: '16px',
  },
  input: {
    width: '100%',
    background: 'rgba(77,214,255,.04)',
    color: '#eaf2fb',
    border: '1px solid rgba(77,214,255,.25)',
    padding: '12px 16px',
    fontFamily: "'Saira Condensed', sans-serif",
    fontSize: '15px',
    outline: 'none',
  },
  btnPrimary: {
    width: '100%',
    fontFamily: "'Orbitron', sans-serif",
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    background: 'linear-gradient(135deg, #ff7a18, #d8500a)',
    color: '#fff5e8',
    border: 'none',
    padding: '12px 20px',
    cursor: 'pointer',
    boxShadow: '0 0 14px rgba(255,122,24,.5), inset 0 1px 0 rgba(255,255,255,.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  btnSecondary: {
    fontFamily: "'Saira Condensed', sans-serif",
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    background: 'rgba(77,214,255,.06)',
    color: '#4dd6ff',
    border: '1px solid rgba(77,214,255,.25)',
    padding: '10px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  btnNav: {
    color: '#5a6470',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: "'Saira Condensed', sans-serif",
    fontSize: '14px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '0 10px',
    height: '22px',
    fontFamily: "'Orbitron', sans-serif",
    fontSize: '10px',
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: '#4dd6ff',
    background: 'rgba(77,214,255,.08)',
    border: '1px solid rgba(77,214,255,.4)',
  },
}

export default function Profile() {
  const { user, profile } = useAuth()
  const [displayName, setDisplayName] = useState('')
  const [callsign, setCallsign] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [success, setSuccess] = useState(false)
  const [pushEnabled, setPushEnabled] = useState(false)
  const [pushLoading, setPushLoading] = useState(false)
  // Per-category notification preferences mirror the notification_preferences
  // table. Lazily upserted on first toggle. Defaults match the column
  // defaults so a missing row reads identically to an unconfigured user.
  const NOTIF_DEFAULTS = {
    kill_pending:    true,
    bounty_on_you:   true,
    frago_assigned:  true,
    frago_broadcast: true,
    intel_found:     true,
    game_status:     true,
    teammate_killed: false,
    bounty_claimed:  false,
  }
  const [notifPrefs, setNotifPrefs] = useState(NOTIF_DEFAULTS)
  const [notifLoading, setNotifLoading] = useState(false)
  // Display mode persisted to localStorage under a per-user key so the
  // choice survives refresh and doesn't leak across account switches on
  // the same browser. Initialised from the user-keyed entry; falls back
  // to dark when no preference has been stored yet.
  const [displayMode, setDisplayMode] = useState(() => getStoredMode(user?.id))

  useEffect(() => { if (profile) { setDisplayName(profile.display_name || ''); setCallsign(profile.callsign || '') } }, [profile])
  useEffect(() => { isPushRegistered().then(setPushEnabled) }, [])
  useEffect(() => {
    if (!user?.id) return
    supabase.from('notification_preferences').select('*').eq('user_id', user.id).limit(1)
      .then(({ data }) => {
        if (data?.[0]) setNotifPrefs({ ...NOTIF_DEFAULTS, ...data[0] })
      })
  }, [user?.id])
  useEffect(() => {
    // Apply mode to <html> data-attr so themeColor.js (and CSS rules)
    // pick it up without prop-drilling. Persistence is per-user via
    // displayMode helpers so two accounts on the same browser don't
    // share preferences.
    applyMode(displayMode)
    if (user?.id) setStoredMode(user.id, displayMode)
  }, [displayMode, user?.id])

  // When the signed-in user changes (re-login on the same tab), pull
  // their stored preference and apply it. Without this an account switch
  // without a full reload would keep showing the previous choice.
  useEffect(() => {
    if (!user?.id) return
    const next = getStoredMode(user.id)
    setDisplayMode(next)
    applyMode(next)
  }, [user?.id])

  const updateNotifPref = async (key, value) => {
    setNotifPrefs(prev => ({ ...prev, [key]: value }))
    setNotifLoading(true)
    // Upsert lazily — INSERT … ON CONFLICT DO UPDATE on user_id.
    const next = { ...notifPrefs, [key]: value, user_id: user.id, updated_at: new Date().toISOString() }
    const { error } = await supabase.from('notification_preferences').upsert(next, { onConflict: 'user_id' })
    if (error) {
      console.warn('[Profile] notif pref save failed:', error)
      // Revert UI on failure so the toggle reflects DB truth.
      setNotifPrefs(prev => ({ ...prev, [key]: !value }))
    } else {
      // Bust the foreground-notify prefs cache so the next realtime emit
      // reads the new toggle state without waiting for a refresh.
      clearForegroundNotifyCache()
    }
    setNotifLoading(false)
  }

  const handleSave = async (e) => {
    e.preventDefault(); setLoading(true); setMessage(''); setSuccess(false)
    const nameError = validateDisplayName(displayName); if (nameError) { setMessage(nameError); setLoading(false); return }
    const callsignError = validateCallsign(callsign); if (callsignError) { setMessage(callsignError); setLoading(false); return }
    const { error } = await supabase.from('users').update({ display_name: displayName.trim(), callsign: callsign.trim() || null }).eq('id', user.id)
    if (error) { setMessage(error.message) } else { setSuccess(true); setMessage('Profile updated') }
    setLoading(false)
  }

  const downloadQR = () => {
    const svg = document.getElementById('player-qr-code')
    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas'); canvas.width = 400; canvas.height = 480
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#0e1116'; ctx.fillRect(0, 0, canvas.width, canvas.height)
    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, 50, 40, 300, 300)
      ctx.fillStyle = '#eaf2fb'; ctx.font = 'bold 28px sans-serif'; ctx.textAlign = 'center'
      ctx.fillText(profile?.display_name || user?.email, 200, 390)
      if (profile?.callsign) { ctx.font = '20px sans-serif'; ctx.fillStyle = '#9aa6b0'; ctx.fillText(`"${profile.callsign}"`, 200, 425) }
      ctx.font = 'bold 14px sans-serif'; ctx.fillStyle = '#ff7a18'; ctx.fillText('DEATH CARD', 200, 465)
      const link = document.createElement('a'); link.download = `death-card-${profile?.display_name || 'player'}.png`; link.href = canvas.toDataURL('image/png'); link.click()
    }
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' }); img.src = URL.createObjectURL(svgBlob)
  }

  const togglePush = async () => {
    setPushLoading(true)
    if (pushEnabled) { await unregisterPushNotifications(user.id); setPushEnabled(false) }
    else { const s = await registerPushNotifications(user.id); setPushEnabled(s) }
    setPushLoading(false)
  }

  const goBack = () => { window.location.href = isPlatformOrGlobalGm(profile) ? '/dashboard' : '/play' }

  return (
    <div style={A.page}>
      <nav style={A.nav}>
        <button onClick={goBack} style={A.btnNav}><ArrowLeft size={14} /> Back</button>
        <span style={{ ...A.display, fontSize: '15px', color: '#ff7a18' }}>◆ ANUBIS</span>
      </nav>

      <main className="max-w-lg mx-auto p-6">
        <h2 className="text-2xl font-bold mb-1" style={A.display}>EDIT PROFILE</h2>
        <p className="text-sm mb-6" style={A.muted}>How you appear to other operators</p>

        <div style={A.panel}>
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 flex items-center justify-center text-2xl font-bold"
              style={{ background: 'linear-gradient(135deg, #ff7a18, #d8500a)', color: '#fff5e8', fontFamily: "'Orbitron', sans-serif" }}>
              {displayName ? displayName[0].toUpperCase() : '?'}
            </div>
            <div>
              <p className="font-bold text-lg" style={{ ...A.display, fontSize: '18px' }}>{displayName || 'No display name'}</p>
              <p className="text-sm" style={A.muted}>{callsign ? `"${callsign}"` : 'No callsign set'}</p>
              <p className="text-xs mt-1" style={A.faded}>{user?.email}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm mb-1 block" style={{ ...A.muted, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '11px' }}>Display Name</label>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" maxLength={32} style={A.input} />
            </div>
            <div>
              <label className="text-sm mb-1 block" style={{ ...A.muted, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '11px' }}>
                Callsign <span style={A.faded}>(optional)</span>
              </label>
              <input type="text" value={callsign} onChange={(e) => setCallsign(e.target.value)} placeholder="e.g. Dax, Boone, Sightless" maxLength={20} style={A.input} />
              <p className="text-xs mt-1" style={A.faded}>Shown on bounties and kill feed</p>
            </div>
            {message && <p className={`text-sm text-center ${success ? '' : ''}`} style={{ color: success ? '#4dd6ff' : '#ff4136' }}>{message}</p>}
            <button onClick={handleSave} disabled={loading} style={{ ...A.btnPrimary, opacity: loading ? 0.5 : 1 }}>
              <Save size={16} /> {loading ? 'SAVING...' : 'SAVE PROFILE'}
            </button>
          </div>
        </div>

        <div style={A.panel}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold" style={{ ...A.display, fontSize: '14px' }}>PUSH NOTIFICATIONS</h3>
              <p className="text-sm mt-1" style={A.muted}>Get notified when a bounty is placed on you</p>
            </div>
            <button onClick={togglePush} disabled={pushLoading}
              style={pushEnabled ? { ...A.btnPrimary, width: 'auto', padding: '8px 16px', fontSize: '12px' } : { ...A.btnSecondary, fontSize: '12px' }}>
              {pushEnabled ? <><Bell size={14} /> ENABLED</> : <><BellOff size={14} /> DISABLED</>}
            </button>
          </div>
        </div>

        <div style={A.panel}>
          <h3 className="font-bold mb-4" style={{ ...A.display, fontSize: '14px' }}>MY DEATH CARD</h3>
          <div className="flex flex-col items-center gap-4">
            {/* Track 3.4 — bare-UUID kill QR (canonical). See PlayerView
                for the same change + parser back-compat note. */}
            <div className="bg-white p-3"><QRCode id="player-qr-code" value={user?.id || ''} size={160} /></div>
            <p className="text-xs text-center" style={A.faded}>Show or print this card so other operators can scan you</p>
            <button onClick={downloadQR} style={A.btnSecondary}><Download size={14} /> DOWNLOAD QR CARD</button>
          </div>
        </div>

        <div style={A.panel}>
          <h3 className="font-bold mb-4" style={{ ...A.display, fontSize: '14px' }}>CAREER STATS</h3>
          <StatsBlock userId={user?.id} />
        </div>

        {/* ── SETTINGS — notifications + display + account ── */}
        <div style={A.panel}>
          <h3 className="font-bold mb-1 flex items-center gap-2" style={{ ...A.display, fontSize: '14px' }}>
            <Settings size={14} /> SETTINGS
          </h3>
          <p className="text-sm mb-4" style={A.muted}>Notifications, display mode, account</p>

          <div className="mb-5">
            <p className="text-xs mb-2" style={{ ...A.muted, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Notifications</p>
            <div style={{ background: 'rgba(77,214,255,.04)', border: '1px solid rgba(77,214,255,.15)' }}>
              {[
                { key: 'kill_pending',    label: 'Kill awaiting honor',    hint: 'Someone scanned you — confirm or dishonor' },
                { key: 'bounty_on_you',   label: 'Bounty placed on you',   hint: 'A GM put a price on your head' },
                { key: 'frago_assigned',  label: 'FRAGO assigned',         hint: 'A mission landed on your node' },
                { key: 'frago_broadcast', label: 'FRAGO broadcast',        hint: 'Mission open to your faction' },
                { key: 'intel_found',     label: 'Intel triggered',        hint: 'Field intel scanned (commander view)' },
                { key: 'game_status',     label: 'Event status changes',   hint: 'GO LIVE, ENDEX, pause, close' },
                { key: 'teammate_killed', label: 'Teammate killed',        hint: 'Off by default — can get noisy' },
                { key: 'bounty_claimed',  label: 'Bounty I issued claimed',hint: 'Off by default — can get noisy' },
              ].map(({ key, label, hint }) => (
                <label key={key} className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                  style={{ borderBottom: '1px solid rgba(77,214,255,.08)' }}>
                  <input
                    type="checkbox"
                    checked={!!notifPrefs[key]}
                    disabled={notifLoading}
                    onChange={e => updateNotifPref(key, e.target.checked)}
                    style={{ accentColor: '#4dd6ff' }}
                  />
                  <span style={{ flex: 1 }}>
                    <span style={{ ...A.text, fontSize: 13 }}>{label}</span>
                    <span style={{ ...A.faded, fontSize: 11, display: 'block' }}>{hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-5">
            <p className="text-xs mb-2" style={{ ...A.muted, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Display mode</p>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { id: 'dark',          label: 'Dark',          icon: Moon, hint: 'Default theme' },
                { id: 'high-contrast', label: 'High Contrast', icon: Sun,  hint: 'Sunlight readability' },
              ].map(opt => {
                const Icon = opt.icon
                const active = displayMode === opt.id
                return (
                  <button key={opt.id}
                    onClick={() => setDisplayMode(opt.id)}
                    style={{
                      flex: 1, padding: '10px 12px',
                      ...A.body, fontSize: 13,
                      background: active ? 'rgba(77,214,255,.12)' : 'rgba(77,214,255,.04)',
                      border: active ? '1px solid #4dd6ff' : '1px solid rgba(77,214,255,.18)',
                      color: active ? '#4dd6ff' : '#9aa6b0',
                      cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    }}
                  >
                    <Icon size={16} />
                    <span style={{ ...A.display, fontSize: 11, letterSpacing: '0.14em' }}>{opt.label}</span>
                    <span style={{ ...A.faded, fontSize: 10 }}>{opt.hint}</span>
                  </button>
                )
              })}
            </div>
            <p className="text-xs mt-2" style={A.faded}>High contrast mode is still rolling out — some screens haven't been retuned yet.</p>
          </div>

          <div>
            <p className="text-xs mb-2" style={{ ...A.muted, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Account</p>
            <div style={{ background: 'rgba(77,214,255,.04)', border: '1px solid rgba(77,214,255,.15)', padding: '10px 12px', marginBottom: 8 }}>
              <p style={{ ...A.faded, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Email</p>
              <p style={{ ...A.text, fontSize: 13 }}>{user?.email}</p>
            </div>
            <div style={{ background: 'rgba(77,214,255,.04)', border: '1px solid rgba(77,214,255,.15)', padding: '10px 12px', marginBottom: 8 }}>
              <p style={{ ...A.faded, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Callsign</p>
              <p style={{ ...A.text, fontSize: 13 }}>{callsign || <span style={A.faded}>(not set — edit above)</span>}</p>
            </div>
          </div>
        </div>

        <div style={{ ...A.panel, textAlign: 'center' }}>
          <p className="text-sm mb-3" style={A.muted}>Found something broken?</p>
          <a href="https://github.com/Z3r05um5/death-card/issues/new" target="_blank" rel="noopener noreferrer"
            style={{ color: '#ff7a18', fontSize: '14px', fontWeight: 600, letterSpacing: '0.08em' }}>
            REPORT A BUG
          </a>
        </div>

        {/* About — M55 maker's mark, app version, link out. */}
        <div style={{ ...A.panel, padding: 0, background: 'transparent', border: 'none', boxShadow: 'none' }}>
          <p style={{ ...A.muted, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 4, textAlign: 'center' }}>About</p>
          <M55Mark variant="about" />
        </div>
      </main>
    </div>
  )
}

function StatsBlock({ userId }) {
  const [stats, setStats] = useState(null)
  useEffect(() => { if (!userId) return; supabase.from('player_stats').select('*').eq('user_id', userId).limit(1).then(({ data }) => setStats(data?.[0] ?? {})) }, [userId])
  if (stats === null) return <p className="text-sm" style={{ color: '#5a6470' }}>Loading stats...</p>
  const items = [
    { val: stats.total_points ?? 0, label: 'Pts', color: '#ff7a18' },
    { val: stats.career_kills ?? 0, label: 'Kills', color: '#eaf2fb' },
    { val: stats.career_deaths ?? 0, label: 'Deaths', color: '#eaf2fb' },
    { val: stats.career_confirmed_kills ?? 0, label: 'Confirmed', color: '#4dd6ff' },
    { val: stats.bounties_claimed ?? 0, label: 'Claimed', color: '#ff7a18' },
    { val: stats.bounties_survived ?? 0, label: 'Survived', color: '#eaf2fb' },
  ]
  return (
    <div className="grid grid-cols-3 gap-2">
      {items.map((s, i) => (
        <div key={i} className="text-center py-2" style={{ background: 'rgba(77,214,255,.04)', border: '1px solid rgba(77,214,255,.12)' }}>
          <p className="font-bold text-lg leading-tight" style={{ fontFamily: "'Orbitron', sans-serif", color: s.color, fontVariantNumeric: 'tabular-nums' }}>{s.val}</p>
          <p style={{ color: '#5a6470', fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{s.label}</p>
        </div>
      ))}
    </div>
  )
}
